<template>
  <div class="remove-patient-data">
    <Dialog width="520px">
      <template slot="title">
        <div class="title">
          {{ modalRemovePatientData.title }}
        </div>
      </template>

      <template slot="body">
        <div class="warning-text">
          The patient and all his data (evaluations, surveys, results, invoices, documents, chat, portal login)
          will be <b>permanently deleted</b>. This can not be undone.
        </div>
        <div class="description">Patient ID</div>
        <Input outlined
               radius="8"
               appendIcon="mdi-identifier"
               placeholder="Patient ID"
               v-model="patientId"
               class="mb-2"
        />
        <div class="description">Patient First Name</div>
        <Input outlined
               radius="8"
               appendIcon="mdi-account-outline"
               placeholder="First Name"
               v-model="firstName"
               class="mb-2"
        />
        <div class="description">Patient Last Name</div>
        <Input outlined
               radius="8"
               appendIcon="mdi-account-outline"
               placeholder="Last Name"
               v-model="lastName"
        />
        <div v-if="errorMessage" class="error-text">{{ errorMessage }}</div>
      </template>

      <template slot="actions">
        <div class="d-flex justify-space-between w-100 pb-4 px-4">
          <Button dark
                  outlined
                  color="grey"
                  :disabled="saving"
                  @click.native="close"
                  :styles="{ 'border-radius': '8px' }"
          >
            <span style="color: #425466">Close</span>
          </Button>
          <Button color="#E53E3E"
                  :dark="canConfirm"
                  :disabled="!canConfirm"
                  @click.native="confirm"
                  :styles="{ 'border-radius': '8px' }"
          >
            <span>Delete Permanently</span>
          </Button>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import Dialog from '@/components/layouts/dialog'
import Button from '@/components/elements/Button'
import Input from '@/components/elements/Input'

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export default {
  name: 'remove-patient-data',
  components: { Dialog, Button, Input },
  data: () => ({
    patientId: '',
    firstName: '',
    lastName: '',
    errorMessage: '',
    saving: false
  }),
  methods: {
    ...mapMutations('dialogs', ['TOGGLE_DIALOG']),
    ...mapActions('organizations', ['removePatientData']),
    close () {
      // closing during the request would lose the result message and the page refresh
      if (this.saving) return
      this.TOGGLE_DIALOG({ name: 'modalRemovePatientData', isOpen: false })
    },
    async confirm () {
      if (!this.canConfirm) return
      if (!GUID_PATTERN.test(this.patientId.trim())) {
        this.errorMessage = 'Patient ID is not valid.'
        return
      }
      this.errorMessage = ''
      this.saving = true
      try {
        const res = await this.removePatientData({
          patientId: this.patientId.trim(),
          organizationId: this.modalRemovePatientData.organizationid,
          firstName: this.firstName,
          lastName: this.lastName
        })
        if (res?.success) {
          this.saving = false
          this.$emit('showPopup', { type: 'remove-patient-data', data: res })
          this.close()
        } else {
          // refused (name mismatch, other organization ...) - keep the dialog open to correct the input
          this.errorMessage = res?.message || 'Patient data was not removed.'
        }
      } finally {
        this.saving = false
      }
    }
  },
  computed: {
    ...mapState({
      modalRemovePatientData: state => state.dialogs.modalRemovePatientData
    }),
    canConfirm () {
      // a patient may have no first or last name - one of them is required, the backend compares both
      return !this.saving && !!this.patientId.trim() && (!!this.firstName.trim() || !!this.lastName.trim())
    }
  }
}
</script>

<style lang="scss" scoped>
.w-100 {
  width: 100%;
}
.remove-patient-data {
  ::v-deep .v-dialog__container {
    .v-card {
      &__title {
        padding: 25px 35px 17px 26px !important;
        justify-content: center !important;
        position: relative;
        .title {
          font-family: 'Poppins';
          font-weight: 600;
          font-size: 16px;
          text-align: center;
          color: #27272E;
        }
        .v-icon {
          display: none;
        }
      }
      &__text {
        .input {
          padding: 0 24px 6px !important;
        }
        .description {
          font-family: 'Poppins';
          padding: 0 24px 4px;
          font-weight: 400;
          font-size: 14px;
          color: #425466;
        }
        .warning-text {
          font-family: 'Poppins';
          margin: 0 24px 16px;
          padding: 10px 12px;
          border-radius: 8px;
          background: #FFF5F5;
          color: #C53030;
          font-size: 13px;
        }
        .error-text {
          font-family: 'Poppins';
          padding: 4px 24px 0;
          color: #E53E3E;
          font-size: 13px;
        }
      }
    }
  }
}
</style>
